import React, { useEffect, useMemo, useRef } from 'react';
import { Field, reduxForm, getFormValues } from 'redux-form';
import { Button } from 'reactstrap';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import validate from './validateProvider';
import renderField from '../../../shared/components/custom/Field';
import { CREATABLE_PROVIDER_TYPES } from './validateProvider';
import { partial } from '../../../util/translation/translation';
import renderSelectField from '../../../shared/components/form/Select';
import SwitchRow from '../../../shared/components/custom/SwitchRow';
import ChoiceCards from '../../../shared/components/custom/ChoiceCards';
import FormCard from '../../../shared/components/custom/FormCard';
import InfoBox from '../../../shared/components/custom/InfoBox';
import { PROVIDER_TYPES } from './NewsProvidersList';

const NewsProviderForm = ({
  handleSubmit,
  categories,
  initialize,
  providerData,
  notificationLabels,
  formValues,
  isCreate,
  onDelete,
}) => {
  const s = partial('shared');
  const p = partial('NewsForm');
  const categoryOptions = useMemo(
    () =>
      [...categories, { name: s('notSelected') }].map((item) => ({
        label: item.name,
        value: item.id,
      })),
    // s is a new function reference every render (partial() isn't memoized);
    // including it would recompute categoryOptions every render, which in
    // turn re-triggers the initialize effect below on every render.
    // eslint-disable-next-line
    [categories]
  );
  const labelOptions = useMemo(
    () => [
      { label: s('notSelected'), value: null },
      ...(notificationLabels || []).map((label) => ({
        label: label.title,
        value: label.id,
      })),
    ],
    // s is redefined every render (see categoryOptions)
    // eslint-disable-next-line
    [notificationLabels]
  );
  const createInitialized = useRef(false);
  const typeOptions = useMemo(
    () =>
      CREATABLE_PROVIDER_TYPES.map((type) => ({
        label: p(`providerType-${type}`),
        value: type,
      })),
    // p is redefined every render (see categoryOptions)
    // eslint-disable-next-line
    []
  );
  useEffect(() => {
    if (isCreate) {
      // Once: labels and categories load later and would clear what was typed
      if (createInitialized.current) {
        return;
      }
      createInitialized.current = true;
      initialize({
        type: typeOptions[0],
        url: '',
        category: { label: s('notSelected') },
        pushEnabled: false,
        notificationLabel: labelOptions[0],
      });
      return;
    }
    const currentCategory = providerData?.category?.id;
    initialize({
      category: categoryOptions.find(
        (item) => item.value === currentCategory
      ) || { label: s('notSelected') },
      pushEnabled: providerData?.pushEnabled,
      notificationLabel:
        labelOptions.find(
          (item) =>
            item.value && item.value === providerData?.notificationLabelId
        ) || labelOptions[0],
    });
    // initialize is a stable redux-form dispatch and s is redefined every
    // render; depending on them would re-run this effect on every render.
    // eslint-disable-next-line
  }, [providerData, categoryOptions, labelOptions, isCreate]);
  const typeLabel = (type) =>
    PROVIDER_TYPES.includes(type) ? p(`providerType-${type}`) : type;

  return (
    <form onSubmit={handleSubmit} className="form settings-form">
      <div className="user-layout">
        <FormCard title={p('feed')}>
          {isCreate ? (
            <>
              <div className="form__form-group">
                <span className="form-header">{p('providerType')}</span>
                {/* The form keeps the type as a select option ({ label, value }) */}
                <Field
                  name="type"
                  component={ChoiceCards}
                  options={typeOptions}
                  ariaLabel={p('providerType')}
                  format={(option) => option?.value}
                  parse={(value) =>
                    typeOptions.find((option) => option.value === value)
                  }
                />
              </div>
              <div className="form__form-group">
                <span className="form-header">{p('feedUrl')}</span>
                <Field
                  name="url"
                  component={renderField}
                  placeholder="https://"
                />
                <span className="settings-form__help">{p('feedUrlInfo')}</span>
              </div>
              {/* Adding a feed publishes its latest articles right away */}
              <InfoBox className="mb-0">{p('providerCreateWarning')}</InfoBox>
            </>
          ) : (
            // The type and address can't be changed after the feed is added
            <dl className="settings-form__facts">
              <div>
                <dt>{p('providerType')}</dt>
                <dd>{typeLabel(providerData?.type) || '–'}</dd>
              </div>
              <div>
                <dt>{p('feedUrl')}</dt>
                <dd className="settings-form__url">
                  {providerData?.config?.url || '–'}
                </dd>
              </div>
            </dl>
          )}
        </FormCard>

        <FormCard title={p('inTheApp')}>
          <div className="form__form-group">
            <span className="form-header">{s('newsCategory')}</span>
            <Field
              name="category"
              placeholder={s('newsCategory')}
              options={categoryOptions}
              component={renderSelectField}
            />
            <span className="settings-form__help">{p('categoryHelp')}</span>
          </div>
          <Field
            name="pushEnabled"
            component={SwitchRow}
            label={s('sendPushNotification')}
            description={p('pushEnabledDescription')}
          />
          {formValues?.pushEnabled && notificationLabels && (
            <div className="form__form-group mt-3 mb-0">
              <span className="form-header">{s('notificationLabel')}</span>
              <Field
                name="notificationLabel"
                component={renderSelectField}
                options={labelOptions}
              />
              <span className="settings-form__help">
                {p('pushLabelDescription')}{' '}
                {/* New tab: this form keeps what's been filled in, and the
                    labels reload when you come back to it */}
                <Link
                  to="/notification_label/create"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {p('createNotificationLabel')}
                </Link>
              </span>
            </div>
          )}
          {isCreate && formValues?.pushEnabled && (
            <InfoBox className="mt-3 mb-0">
              {p('providerCreatePushWarning')}
            </InfoBox>
          )}
        </FormCard>
      </div>

      <div className="messages-form__footer">
        {isCreate ? (
          <span />
        ) : (
          <Button
            type="button"
            outline
            color="danger"
            size="sm"
            className="settings-form__delete mb-0"
            onClick={onDelete}
          >
            <DeleteOutlineIcon />
            {p('deleteProvider')}
          </Button>
        )}
        <div className="messages-form__secondary">
          <Link
            to="/news?tab=2"
            className="btn btn-outline-secondary btn-sm mb-0"
          >
            {s('close')}
          </Link>
          <Button type="submit" color="primary" size="sm" className="mb-0">
            {isCreate ? p('addProvider') : s('save')}
          </Button>
        </div>
      </div>
    </form>
  );
};

const reduxF = reduxForm({
  form: 'news_provider_form',
  validate,
});

export default withRouter(
  connect((state) => ({
    formValues: getFormValues('news_provider_form')(state),
  }))(reduxF(NewsProviderForm))
);
